/**
 * Migrar um veículo ou um motor para outro — juntar dois cadastros que são a
 * mesma coisa.
 *
 * **É a operação mais perigosa do sistema, e a única que reescreve chave em vinte
 * tabelas.** Migrar veículo mexe em 21, migrar motor em 18, tudo numa transação;
 * três delas apagam linhas antes, porque a equivalente já existe no destino. Nada
 * disso tem volta.
 *
 * **Por isso a tela confere primeiro.** A conferência conta, tabela a tabela, o
 * que mudaria de dono e o que seria apagado — sem gravar. Só depois de ler é que
 * aparece o gesto de aplicar, e ele pede o nome da origem digitado.
 *
 * **A conferência já pagou por si três vezes.** Descobriu que migrar veículo nunca
 * completava, porque três nomes de tabela na lista da API não existiam no banco.
 * Descobriu que três tabelas carregam o id migrado dentro da chave primária e não
 * apagam o duplicado antes — o `UPDATE` viola a chave e desfaz tudo, em pares de
 * cadastros que existem hoje. E descobriu que dez tabelas continuam apontando para
 * a origem depois da fusão, porque o banco não declara chave estrangeira nenhuma
 * e a lista da migração sempre foi convenção.
 *
 * **Os dois primeiros desligam o aplicar; o terceiro é informação.** Ficar para
 * trás não impede a migração — só parte o conteúdo entre os dois cadastros, e quem
 * decide isso é quem está olhando.
 *
 * **A origem não é apagada.** O resumo das rotas da API dizia que sim; não diz
 * mais. O cadastro fica, sem vínculo nenhum — excluí-lo é um segundo gesto, no
 * formulário ao lado.
 */

import { useState } from "react"
import {
  impedimentos, usarConferirMigracao, usarMigrar,
  type ConferenciaDaMigracao, type TipoDeMigracao,
} from "@/api/manutencao"
import { ConfirmacaoDigitada } from "@/kit/ConfirmacaoDigitada"

export function MigrarCadastro({ tipo, origemId, origemNome, aoAvisar }: {
  tipo: TipoDeMigracao
  origemId: number
  origemNome: string
  aoAvisar: (m: string | null) => void
}) {
  const [aberto, setAberto] = useState(false)
  const [destinoId, setDestinoId] = useState("")
  const [pedido, setPedido] = useState(0)
  const [confirmando, setConfirmando] = useState(false)

  const destino = Number(destinoId)
  const conferencia = usarConferirMigracao(tipo, origemId, destino, pedido > 0)
  const migrar = usarMigrar(tipo)

  const c = conferencia.data
  const impede = impedimentos(c)
  const oQue = tipo === "veiculo" ? "veículo" : "motor"

  if (!aberto) {
    return (
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line2)" }}>
        <button
          onClick={() => setAberto(true)}
          style={{
            border: "1px solid var(--line)", borderRadius: 6, padding: "5px 11px",
            fontSize: 12, cursor: "pointer", background: "var(--surface)", color: "var(--txt2)",
          }}
        >
          Migrar este {oQue} para outro…
        </button>
        <p style={{ margin: "5px 0 0", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
          Junta dois cadastros que são a mesma coisa: tudo o que aponta para este passa a apontar
          para o outro. Não tem volta — a tela confere antes.
        </p>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line2)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <b style={{ fontSize: 12 }}>Migrar {oQue}</b>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>
          {origemNome} (#{origemId}) → …
        </span>
        <button onClick={() => { setAberto(false); setConfirmando(false); setPedido(0) }}
                style={{
                  marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
                  color: "var(--txt3)", fontSize: 14, lineHeight: 1,
                }}>×</button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 10 }}>
        <label style={{ fontSize: 10.5, color: "var(--txt2)" }}>
          Id do {oQue} de destino<br />
          <input
            value={destinoId}
            onChange={(e) => { setDestinoId(e.target.value.replace(/\D/g, "")); setPedido(0); setConfirmando(false) }}
            placeholder="id"
            style={{
              width: 110, padding: "5px 8px", fontSize: 12, marginTop: 3,
              border: "1px solid var(--line)", borderRadius: 5,
              background: "var(--surface)", color: "var(--txt)",
            }}
          />
        </label>

        <button
          onClick={() => { aoAvisar(null); setConfirmando(false); setPedido((n) => n + 1) }}
          disabled={destino <= 0 || destino === origemId || conferencia.isFetching}
          style={{
            height: 28, padding: "0 13px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
            border: 0, cursor: "pointer",
            background: destino > 0 && destino !== origemId ? "var(--acao)" : "var(--surface2)",
            color: destino > 0 && destino !== origemId ? "#fff" : "var(--txt3)",
          }}
        >
          {conferencia.isFetching ? "Conferindo…" : "Conferir"}
        </button>

        {destino === origemId && destino > 0 && (
          <span style={{ fontSize: 11, color: "var(--erro)" }}>
            Origem e destino são o mesmo {oQue}.
          </span>
        )}
      </div>

      {pedido > 0 && conferencia.error && (
        <p style={{ margin: 0, fontSize: 11.5, color: "var(--erro)" }}>
          {conferencia.error instanceof Error ? conferencia.error.message : "Não foi possível conferir."}
        </p>
      )}

      {c && <Relatorio c={c} />}

      {c && !confirmando && (
        <button
          onClick={() => setConfirmando(true)}
          disabled={impede.length > 0}
          title={impede.length > 0
            ? `A migração vai estourar e desfazer tudo: ${impede.join("; ")}.`
            : undefined}
          style={{
            marginTop: 10, height: 28, padding: "0 13px", borderRadius: 7,
            fontSize: 12.5, fontWeight: 600, border: 0,
            cursor: impede.length > 0 ? "default" : "pointer",
            background: impede.length > 0 ? "var(--surface2)" : "var(--erro)",
            color: impede.length > 0 ? "var(--txt3)" : "#fff",
          }}
        >
          Migrar mesmo assim
        </button>
      )}

      {c && confirmando && (
        <div style={{ marginTop: 10 }}>
          <ConfirmacaoDigitada
            alvo={c.origem}
            rotuloDoBotao={`Migrar para ${c.destino}`}
            ocupado={migrar.isPending}
            impedido={impede.length > 0
              ? <>A migração não completaria — {impede.join("; ")}.</>
              : undefined}
            oQueAcontece={
              <>
                <b>{c.totalMovido.toLocaleString("pt-BR")} linha(s)</b> deixam de apontar para{" "}
                <b>{c.origem}</b> e passam a apontar para <b>{c.destino}</b>, em{" "}
                {c.movidas.filter((m) => m.linhas > 0).length} tabela(s).
                {c.totalRemovido > 0 && (
                  <> <b style={{ color: "var(--erro)" }}>
                    {c.totalRemovido.toLocaleString("pt-BR")} linha(s) são apagadas</b> antes, porque
                    o destino já tem a equivalente.
                  </>
                )}
                {" "}Isto <b>não tem volta</b>.
              </>
            }
            aoConfirmar={() => {
              aoAvisar(null)
              migrar.mutate({ origemId, destinoId: destino }, {
                onSuccess: (r) => {
                  aoAvisar(r.message ?? "Migração concluída.")
                  setConfirmando(false); setPedido(0); setAberto(false)
                },
                onError: (e) => aoAvisar(e instanceof Error ? e.message : "Não foi possível migrar."),
              })
            }}
            aoCancelar={() => setConfirmando(false)}
          />
        </div>
      )}
    </div>
  )
}

function Relatorio({ c }: { c: ConferenciaDaMigracao }) {
  const movidas = c.movidas.filter((m) => m.linhas > 0)
  const faltam = c.movidas.filter((m) => m.linhas < 0)

  return (
    <div style={{
      marginTop: 4, padding: 10, borderRadius: 6,
      border: "1px solid var(--line2)", background: "var(--surface2)",
    }}>
      <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.55 }}>
        <b>{c.origem}</b> (#{c.origemId}) → <b>{c.destino}</b> (#{c.destinoId}) ·{" "}
        {c.totalMovido.toLocaleString("pt-BR")} linha(s) mudam de dono
        {c.totalRemovido > 0 && <> · <b style={{ color: "var(--erro)" }}>
          {c.totalRemovido.toLocaleString("pt-BR")} apagadas</b></>}
      </p>

      {c.avisos.map((a, i) => (
        <p key={i} style={{
          margin: "0 0 7px", padding: "6px 9px", borderRadius: 5, fontSize: 11,
          lineHeight: 1.5,
          background: a.includes("NÃO VAI COMPLETAR") ? "color-mix(in srgb, var(--erro) 12%, transparent)" : "transparent",
          color: a.includes("NÃO VAI COMPLETAR") ? "var(--erro)" : "var(--txt2)",
          border: a.includes("NÃO VAI COMPLETAR") ? "1px solid var(--erro)" : "0",
        }}>
          {a}
        </p>
      ))}

      {movidas.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 14px", marginTop: 6 }}>
          {movidas.map((m) => (
            <span key={m.tabela} style={{ fontSize: 10.5, color: "var(--txt2)" }}>
              <span className="mono">{m.tabela}</span>{" "}
              <b style={{ fontVariantNumeric: "tabular-nums" }}>{m.linhas.toLocaleString("pt-BR")}</b>
            </span>
          ))}
        </div>
      )}

      {c.removidas.length > 0 && (
        <div style={{ marginTop: 7 }}>
          {c.removidas.map((r) => (
            <p key={r.tabela} style={{ margin: 0, fontSize: 10.5, color: "var(--erro)", lineHeight: 1.5 }}>
              <span className="mono">{r.tabela}</span>: {r.linhas.toLocaleString("pt-BR")} apagadas — {r.porque}
            </p>
          ))}
        </div>
      )}

      {c.colisoes.length > 0 && (
        <div style={{ marginTop: 7 }}>
          {c.colisoes.map((x) => (
            <p key={x.tabela} style={{ margin: 0, fontSize: 10.5, color: "var(--erro)", lineHeight: 1.5 }}>
              <span className="mono">{x.tabela}</span>: {x.linhas.toLocaleString("pt-BR")} linha(s)
              já existem no destino com a mesma chave (<span className="mono">{x.chave}</span>) —
              e esta tabela não apaga o duplicado antes.
            </p>
          ))}
        </div>
      )}

      {faltam.length > 0 && (
        <p style={{ margin: "7px 0 0", fontSize: 10.5, color: "var(--erro)", lineHeight: 1.5 }}>
          Não existem no banco:{" "}
          {faltam.map((m) => <span key={m.tabela} className="mono">{m.tabela} </span>)}
        </p>
      )}

      {c.ficam.length > 0 && (
        <div style={{ marginTop: 9, paddingTop: 7, borderTop: "1px dashed var(--line2)" }}>
          <p style={{ margin: "0 0 3px", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
            Ficam apontando para o cadastro antigo — a migração não toca nestas, e nem o sistema
            antigo tocava:
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 14px" }}>
            {c.ficam.map((f) => (
              <span key={f.tabela} style={{ fontSize: 10.5, color: "var(--txt3)" }}>
                <span className="mono">{f.tabela}</span>{" "}
                <b style={{ fontVariantNumeric: "tabular-nums" }}>{f.linhas.toLocaleString("pt-BR")}</b>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
